import React, { useEffect, useRef, useState } from "react";
import { MoreVertical, FileText, Trash2, Archive, ListChecks, UserPlus, Loader2, Check, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { ChatArchiveActions } from "./ConversationTools";

// Vertical three-dot menu of a chat room: every secondary header action lives here so the title keeps its room.
export function ChatHeaderMenu({ conv, personas, onAdded, onArchiveDone, onClear, onNotes, savingNotes }) {
  const [open, setOpen] = useState(false);
  const [invite, setInvite] = useState(false);
  const [busy, setBusy] = useState("");
  const ref = useRef(null);
  const nav = useNavigate();
  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setInvite(false); } };
    document.addEventListener("pointerdown", h);
    return () => document.removeEventListener("pointerdown", h);
  }, [open]);
  const builtin = !!conv.members?.[0]?.builtin;
  const inConv = new Set((conv.persona_ids || []).concat((conv.members || []).map((m) => m.id)));
  const options = (personas || []).filter((p) => !inConv.has(p.id) && !p.builtin);
  const add = async (p) => {
    setBusy(p.id);
    try { const r = await api.post(`/conversations/${conv.id}/personas`, { persona_id: p.id }); onAdded?.(r.data); toast.success(`${p.name} bergabung`); setOpen(false); setInvite(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah asisten"); } finally { setBusy(""); }
  };
  const item = "flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50";
  const close = (fn) => () => { setOpen(false); fn?.(); };
  return (
    <ChatArchiveActions cid={conv.id} onDone={onArchiveDone} render={({ archive, askSummary, busy: b }) => (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} title="Menu percakapan" aria-label="Menu percakapan" data-testid="chat-menu-btn" className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E6EAF2] bg-white text-slate-600 hover:bg-slate-50"><MoreVertical size={17} /></button>
      {open && (
        <div className="absolute right-0 top-11 z-40 w-72 rounded-2xl border border-[#E7ECF3] bg-white p-2 shadow-xl" data-testid="chat-menu">
          {conv.room_kind === "task" && conv.task_id && <button onClick={close(() => nav(`/workspace/${conv.task_id}`))} className={item} data-testid="chat-menu-open-task"><ExternalLink size={15} className="text-[#2F6BFF]" /> Buka tugas di Ruang Kerja</button>}
          {!builtin && (
            <div>
              <button onClick={() => setInvite((v) => !v)} className={item} data-testid="chat-menu-invite"><UserPlus size={15} className="text-slate-500" /> Undang asisten lain</button>
              {invite && (
                <div className="ml-6 mb-1 space-y-0.5 border-l border-[#E7ECF3] pl-2" data-testid="add-persona-menu">
                  {options.length === 0 && <p className="px-2 py-1.5 text-xs text-slate-500">Semua asisten sudah ada di percakapan ini.</p>}
                  {options.map((p) => (
                    <button key={p.id} onClick={() => add(p)} disabled={!!busy} data-testid={`add-persona-${p.id}`} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-slate-50 disabled:opacity-50">
                      {p.portrait ? <img src={p.portrait} alt="" className="h-6 w-6 rounded-full object-cover" /> : <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#2F6BFF] text-[10px] font-bold text-white">{p.name?.[0]}</span>}
                      <span className="min-w-0 flex-1 truncate font-semibold text-slate-800" data-no-i18n>{p.name}</span>
                      {busy === p.id ? <Loader2 size={13} className="animate-spin text-slate-400" /> : <Check size={13} className="text-slate-300" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {conv.type !== "private" && <button onClick={close(onNotes)} disabled={savingNotes} className={item} data-testid="save-notes-btn">{savingNotes ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} className="text-slate-500" />} Buat notulen ke Ruang Kerja</button>}
          {!builtin && <>
            <button onClick={() => archive().then(() => setOpen(false))} disabled={!!b} className={item} data-testid="archive-chat-btn">{b === "archive" ? <Loader2 size={15} className="animate-spin" /> : <Archive size={15} className="text-slate-500" />} Arsipkan percakapan</button>
            <button onClick={() => { setOpen(false); askSummary(); }} disabled={!!b} className={item} data-testid="compress-chat-btn"><ListChecks size={15} className="text-slate-500" /> Ringkas & padatkan percakapan</button>
          </>}
          <div className="my-1 border-t border-[#E7ECF3]" />
          <button onClick={close(onClear)} className={`${item} text-[#EF4444] hover:bg-[#EF4444]/10`} data-testid="clear-chat-btn"><Trash2 size={15} /> Hapus riwayat obrolan</button>
        </div>
      )}
    </div>
    )} />
  );
}
